import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { characterService } from '../services/character.service';
import { QUERY_KEYS } from '../constants';
import type { ApiResponse } from '../types/common';
import type {
  GetAllCharactersResponse,
  GetCharactersParams,
  GetVotingCharactersResponse,
  SubmitVoteRequest,
  CreateCharacterRequest,
  GetVotingStatsResponse,
} from '../types/character.types';

/**
 * Hook to fetch all available characters with optional filters
 */
export const useCharacters = (
  params?: GetCharactersParams
): UseQueryResult<ApiResponse<GetAllCharactersResponse>, Error> => {
  return useQuery({
    queryKey: [...QUERY_KEYS.CHARACTER.ALL, params],
    queryFn: () => characterService.getAllCharacters(params),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch characters in voting phase
 */
export const useVotingCharacters = (): UseQueryResult<ApiResponse<GetVotingCharactersResponse>, Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.CHARACTER.VOTING,
    queryFn: () => characterService.getVotingCharacters(),
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Hook to submit a vote for a character
 */
export const useSubmitVote = (): UseMutationResult<
  ApiResponse<{ message: string }>,
  Error,
  SubmitVoteRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SubmitVoteRequest) => characterService.submitVote(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHARACTER.VOTING });
    },
  });
};

/**
 * Hook to create a character (admin only)
 */
export const useCreateCharacter = (): UseMutationResult<
  ApiResponse<{ _id: string }>,
  Error,
  CreateCharacterRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateCharacterRequest) => characterService.createCharacter(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHARACTER.ALL });
    },
  });
};

/**
 * Hook to get voting stats (admin only)
 */
export const useVotingStats = (enabled = true): UseQueryResult<ApiResponse<GetVotingStatsResponse>, Error> => {
  return useQuery({
    queryKey: [...QUERY_KEYS.CHARACTER.VOTING, 'stats'],
    queryFn: () => characterService.getVotingStats(),
    enabled,
    staleTime: 1000 * 30,
  });
};

/**
 * Hook to toggle voting (admin only)
 */
export const useToggleVoting = (): UseMutationResult<
  ApiResponse<{ message: string }>,
  Error,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => characterService.toggleVoting(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHARACTER.VOTING });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.CHARACTER.VOTING, 'stats'] });
    },
  });
};

/**
 * Hook to finalize voting (admin only)
 */
export const useFinalizeVoting = (): UseMutationResult<
  ApiResponse<{ message: string }>,
  Error,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => characterService.finalizeVoting(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHARACTER.VOTING });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.CHARACTER.VOTING, 'stats'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHARACTER.ALL });
    },
  });
};
